/**
 * +field: live world state for the HUD widgets (hud/field/*), written by the world layer
 * (world/worldApp.ts). Widgets are created by the HUD without constructor arguments, so they poll
 * this object per frame (plain fields, `*Version` counters bump on change) the same way the skill
 * cluster polls meta/inrunHud's `inRunView`. DOM-free.
 */
import type { LevelColor } from '@keystone/progression';

export type MapKind = 'town' | 'field' | 'dungeon';

/** One quest-tracker line (world/trackerModel.ts builds them from the character's bounties). */
export interface TrackerRow {
  id: string;
  kind: 'chain' | 'bounty' | 'dungeon' | 'quest'; // +quests: 'quest' = a side quest
  title: string;
  /** Objective text ("Slay Meadow Slimes"). */
  text: string;
  cur: number;
  max: number;
  /** Ready to turn in / step done. */
  done: boolean;
  /** Where to turn it in, when done ("Return to Elder Rowan"). */
  hint?: string;
  /** The zone's main quest (drawn first and highlighted). */
  main?: boolean;
  /** +quests: the side quest the waypoint arrow follows. */
  tracked?: boolean;
  /** +quests: an outgrown (grey) side quest. */
  grey?: boolean;
}

/** The tracker arrow's target on the current map (the step's spot, or the portal toward it). */
export interface Waypoint {
  pos: { x: number; y: number };
  label: string;
  /** Pointing at a portal on the way (not the objective itself). */
  portal: boolean;
}

export type AutoStatus = 'off' | 'hunting' | 'fighting' | 'resting' | 'reviving' | 'paused' | 'gathering'; // +prof: gathering

/** What the HUD may ask of the world layer (set by worldApp while a world session runs). */
export interface FieldActions {
  toggleAuto(): void;
  /** Talk to / use a town NPC by id. */
  interact(npcId: string): void;
  /** Leave the current dungeon (back to its entrance). */
  leaveDungeon?(): void;
  /** +quests: point the waypoint arrow at a side quest (the same id again: back to the main quest). */
  trackQuest?(id: string): void;
  /** +quests: open / close the quest log. */
  toggleQuestLog?(): void;
}

export interface NpcPrompt {
  npcId: string;
  name: string;
  title: string;
  /** +zone: 'use' (waystones) instead of 'Talk to'. +prof: 'gather' (herb / ore nodes). */
  verb?: 'use' | 'gather';
}

export interface FieldView {
  /** A world session (field, town or levelled dungeon) is running. */
  active: boolean;
  mapKind: MapKind;
  mapId: string;
  mapName: string;
  /** "Lv 1–5" (empty in town). */
  band: string;
  heroName: string;
  classId: string;
  level: number;
  /** XP into the level (profile + kills not yet confirmed), and needed for the next (0 at the cap). */
  xp: number;
  next: number;
  maxed: boolean;
  xpVersion: number;
  tracker: TrackerRow[];
  trackerVersion: number;
  auto: { on: boolean; available: boolean; status: AutoStatus };
  autoVersion: number;
  /** Town NPC in reach (the "[T] Talk" prompt), or null. */
  prompt: NpcPrompt | null;
  promptVersion: number;
  /** Main-quest arrow target on this map (null: none). */
  waypoint: Waypoint | null;
  waypointVersion: number;
  /** The main quest asks for Auto right now (the Z button pulses with a coach bubble). */
  autoHint: boolean;
  /** Abilities unlocked this session and not cast yet (their action-bar slot glows). */
  newUnlocks: Set<string>;
  unlockVersion: number;
  actions: FieldActions | null;
}

export const fieldView: FieldView = {
  active: false,
  mapKind: 'field',
  mapId: '',
  mapName: '',
  band: '',
  heroName: '',
  classId: '',
  level: 1,
  xp: 0,
  next: 0,
  maxed: false,
  xpVersion: 0,
  tracker: [],
  trackerVersion: 0,
  auto: { on: false, available: false, status: 'off' },
  autoVersion: 0,
  prompt: null,
  promptVersion: 0,
  waypoint: null,
  waypointVersion: 0,
  autoHint: false,
  newUnlocks: new Set(),
  unlockVersion: 0,
  actions: null,
};

/** Reset to "no world session" (dispose). */
export function resetFieldView(): void {
  const v = fieldView;
  v.active = false;
  v.actions = null;
  v.prompt = null;
  v.waypoint = null;
  v.waypointVersion++;
  v.autoHint = false;
  v.tracker = [];
  v.auto = { on: false, available: false, status: 'off' };
  v.newUnlocks.clear();
  v.xpVersion++;
  v.trackerVersion++;
  v.autoVersion++;
  v.promptVersion++;
  v.unlockVersion++;
}

/** Nameplate level badge: "Lv 4" (+ elite mark) in the WoW con colour for the hero's level. */
export interface LevelBadge {
  text: string;
  color: LevelColor;
  elite: boolean;
}
